/**
 * BenchmarkLegend — the report glossary. Defines the four classification
 * values and the umbrella "Visibility" term so the report reads with one
 * consistent vocabulary (the team's nomenclature request). Collapsible so it
 * stays out of the way once read.
 *
 * Opens with the metric table from the 9-July-2026 report spec: each metric
 * paired with the business question it answers, so a marketing reader knows not
 * just what a column means but what to do with it.
 *
 * The source-category list is rendered from the shared SOURCE_CATEGORIES single
 * source of truth (never hand-typed) so it can never drift from the classifier.
 */
import { SOURCE_CATEGORIES } from '@aurora/shared';

/**
 * Metric → definition → the question it answers.
 *
 * Joan's wording VERBATIM (17-July-2026 doc, "Recommended revised definitions").
 * Client-facing copy, not developer prose — do not paraphrase or "improve" it.
 * The same four definitions appear as column tooltips in BenchmarkDetail.
 */
const METRICS: Array<[string, string, string]> = [
  [
    'Visibility',
    'The umbrella metric. The company appeared in the AI answer in at least one meaningful way: as a mention, citation, and/or recommendation. Visibility is counted once per answer, even if multiple signals appear.',
    'Are we showing up at all?',
  ],
  [
    'Mention',
    'The answer named the company, whether or not it was also cited or recommended.',
    'Are we being associated with the category?',
  ],
  [
    'Citation',
    'The company had source-backed visibility in the answer, either because it was named with a citation or because AI cited a source owned or published by the company.',
    'Is our owned or published content being used as evidence?',
  ],
  [
    'Recommendation',
    'The company was actively recommended, selected, shortlisted, ranked, or framed as a supplier, product, platform, or tool to consider.',
    'Is AI putting us forward as a supplier choice?',
  ],
];

export function BenchmarkLegend(): JSX.Element {
  return (
    <details className="benchmark-legend card">
      <summary>How to read this report — definitions</summary>

      <table className="leads-table legend-metrics">
        <thead>
          <tr>
            <th>Metric</th>
            <th>Definition</th>
            <th>How to use it</th>
          </tr>
        </thead>
        <tbody>
          {METRICS.map(([metric, definition, use]) => (
            <tr key={metric}>
              <td className="lead-name">{metric}</td>
              <td>{definition}</td>
              <td className="muted">{use}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="muted legend-note">
        <strong>The three signals overlap and do not add up.</strong> Mention,
        Citation and Recommendation are measured <em>independently</em>: a
        single answer that names you, cites your website, and recommends you
        counts <em>once in each</em>. Visibility is their <strong>union</strong>{' '}
        — the answers where you appeared at all — not their sum. So compare
        companies <em>down</em> a column (“who is recommended most?”), never{' '}
        <em>across</em> a row as if the three were slices of a pie.
      </p>
      <p className="muted legend-note">
        Recommendations indicate AI preference, citations indicate source-backed
        authority, and mentions indicate general awareness without clear
        preference. High visibility carried mostly by mentions is a weaker
        position than lower visibility carried by recommendations or citations.
      </p>
      <p className="muted legend-note">
        <strong>Why the citation numbers changed (July 2026).</strong> Until
        recently each answer was filed under its <em>strongest</em> class only,
        so “Citation” actually meant <em>cited but NOT recommended</em> and
        “Mention” meant <em>named but NOT cited and NOT recommended</em>. The
        best possible outcome — AI both citing and recommending you — therefore
        scored <strong>zero</strong> in both columns, and a company could be
        cited in 62 answers while the report said 5. Each signal is now counted
        on its own, so the columns finally say what their names say.
      </p>

      <dl className="legend-list">
        <dt>Scored answer</dt>
        <dd>
          An answer the model actually returned and we classified. Answers that
          came back blank or unusable are “Not Scored” and are{' '}
          <strong>excluded</strong> from the totals — we never count an absent
          answer as a No Mention.
        </dd>
        <dt>Named vs. unnamed competitors</dt>
        <dd>
          <strong>Named</strong> competitors are the ones you listed when you
          created the run — they get a fixed column in every answer’s
          classification. <strong>Unnamed</strong> companies are the ones AI
          surfaced on its own for your categories. Both compete for the same
          answer space; only one set was on your radar.
        </dd>
        <dt>Competitor groups</dt>
        <dd>
          Each competitor you name belongs to one of three groups —{' '}
          <strong>Suppliers</strong>,{' '}
          <strong>Directories &amp; Marketplaces</strong>, and{' '}
          <strong>Editorial &amp; Media Brands</strong> — and the Named
          Competitive Set is grouped by them so a cross-category set reads
          clearly. The group only <em>labels and organizes</em> the table:{' '}
          <strong>naming a competitor tracks it either way</strong>, whatever
          its group, and every count is computed the same way. (A directory or
          media brand AI raises <em>on its own</em> is still treated as a{' '}
          <strong>source</strong>, not a competitor.)
        </dd>
        <dt>Leaders by stage</dt>
        <dd>
          Who leads each buyer-journey stage, split by signal — most mentions,
          most cited, most recommended. Split rather than combined because
          winning on mentions and winning on recommendations are different
          competitive positions. Read each row <em>by stage</em>: these are the
          strongest performers within that stage, not overall rankings.
        </dd>
        <dt>Theme</dt>
        <dd>
          What a prompt is <em>about</em> (a technology, workflow, or
          application), independent of its buyer-journey <em>stage</em>. Themes
          group ~30 prompts into a handful of subjects so a content gap is
          visible as one row instead of scattered across the prompt table.
        </dd>
        <dt>Citation vs. Source type</dt>
        <dd>
          A <strong>citation</strong> is one specific link/reference inside a
          single answer. A <strong>source type</strong> is its category
          (Peer-reviewed / Society Publisher, Supplier Content, Directory /
          Marketplace, Core Facility / Academia, Protocol, …). One source type
          can be cited many times — they are not the same thing. For
          peer-reviewed sources the domain names the <strong>publisher</strong>
          or repository (nature.com, PMC); the specific <strong>
            journal
          </strong>{' '}
          is captured in the source export's Journal column.
        </dd>
        <dt>Supplier landscape</dt>
        <dd>
          Every company the AI named for your categories — the “suppliers AI
          actually surfaces”, tracked or not. Companies are shown by{' '}
          <strong>parent company</strong> (sub-brands fold to their parent).
        </dd>
        <dt>What counts as a supplier</dt>
        <dd>
          A company that{' '}
          <strong>
            sells products, reagents, instruments, or lab services to
            researchers
          </strong>
          . Universities, hospitals, government and non-profit research
          institutes (they buy these products), and pharma / therapeutics
          companies (they make the finished drug, not the research tools) are{' '}
          <strong>not</strong> suppliers and are excluded.
        </dd>
        <dt>Surfaced at each stage</dt>
        <dd>
          The suppliers AI named at each buyer-journey stage — showing that
          suppliers get surfaced <strong>early</strong> (problem definition,
          workflow), not only at the final shortlist. The number is that
          supplier’s <strong>visibility</strong> at that stage (the answers it
          appeared in at all); ★ marks where it was actively{' '}
          <strong>recommended</strong>; bold = your tracked set.
        </dd>
        <dt>Directory / Marketplace</dt>
        <dd>
          A directory / marketplace that helps researchers find or compare
          suppliers (Biocompare, CiteAb, SelectScience…). When AI raises one{' '}
          <em>on its own</em>, it is a{' '}
          <strong>source type, not a supplier</strong>: it is excluded from the
          competitive share-of-voice and reported under “Sources cited”, by
          domain. But a directory you <strong>name as a competitor</strong> is
          tracked like any other company — naming it is the authorization.
          Directories compete with directories, with suppliers, and with media
          brands, so a directory can be benchmarked with a real competitive set
          of its own.
        </dd>
        <dt>Answers citing this source</dt>
        <dd>
          On the source tables, how many scored answers cited a source — counted{' '}
          <strong>once per answer</strong> (breadth). Distinct from{' '}
          <strong>Total citations</strong>, the raw count of individual links
          (volume), which is the number that lines up with the source export. An
          answer citing 7 papers from one journal adds 7 to Total citations but
          1 here.
        </dd>
        <dt>Source category</dt>
        <dd>
          What the AI drew on, using one controlled vocabulary:{' '}
          {SOURCE_CATEGORIES.join(', ')}.
        </dd>
      </dl>
    </details>
  );
}
